import { UploadSimpleIcon } from "@phosphor-icons/react/dist/csr/UploadSimple";
import { WarningIcon } from "@phosphor-icons/react/dist/csr/Warning";
import { useState } from "react";
import { Alert, AlertDescription } from "@volca/ui/components/ui/alert";
import { Button } from "@volca/ui/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@volca/ui/components/ui/dialog";
import { FileUpload } from "@volca/ui/components/ui/file-upload";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { toast } from "@volca/ui/lib/toast";
import { useUploadUsers } from "@/business/query";
import { getErrorMessage } from "@/core/api/errors";
import { CSVValidationError } from "@/core/utils/csv-validation";
import {
  validateTechnicianCSVFile,
  processValidatedTechnicianCSV,
} from "@/core/utils/technician-csv";

interface TechnicianUploadModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onComplete: () => void;
  businessId?: string;
}

interface UploadResult {
  successCount: number;
  failedRows?: Array<{
    row: Record<string, string>;
    errors: string;
  }>;
}

export const TechnicianUploadModal = ({
  isOpen,
  onOpenChange,
  onComplete,
  businessId,
}: TechnicianUploadModalProps) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadResult, setUploadResult] = useState<UploadResult | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [csvValidationErrors, setCsvValidationErrors] = useState<
    CSVValidationError[]
  >([]);

  const { mutate: uploadUsers } = useUploadUsers(businessId || null);

  const handleFileSelect = async (file: File) => {
    const validation = await validateTechnicianCSVFile(file);

    if (!validation.isValid) {
      setCsvValidationErrors(validation.errors);
      toast.error(
        "CSV file validation failed. Please check the file format and required fields.",
      );
      return;
    }

    setCsvValidationErrors([]);
    const processedFile = processValidatedTechnicianCSV(file, validation);
    setSelectedFile(processedFile);
    setUploadResult(null);
  };

  const handleUpload = () => {
    if (!selectedFile || !businessId) return;

    setIsUploading(true);
    uploadUsers(
      { file: selectedFile, welcomeEmail: true },
      {
        onSuccess: (response) => {
          const result: UploadResult = {
            successCount: response.success_count || 0,
            failedRows: response.failed_rows || [],
          };
          setUploadResult(result);
          setIsUploading(false);

          if (result.failedRows && result.failedRows.length > 0) {
            toast.error(
              `Uploaded ${result.successCount} technicians, but ${result.failedRows.length} failed`,
              {
                description: "Review the failed rows and try those again.",
              },
            );
          } else {
            toast.success(
              `Successfully uploaded ${result.successCount} technicians`,
            );
          }
        },
        onError: (error) => {
          setIsUploading(false);
          toast.error("Failed to upload technicians", {
            description: getErrorMessage(error),
          });
          console.error("Upload error:", error);
        },
      },
    );
  };

  const handleComplete = () => {
    setSelectedFile(null);
    setUploadResult(null);
    setCsvValidationErrors([]);
    onComplete();
  };

  const handleClose = () => {
    if (!isUploading) {
      setSelectedFile(null);
      setUploadResult(null);
      setCsvValidationErrors([]);
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogDescription className="sr-only">
          Upload a CSV file with technician details to add them to your account.
        </DialogDescription>
        <DialogHeader>
          <DialogTitle>Upload technicians</DialogTitle>
        </DialogHeader>

        <Stack className="w-full" gap={6} align="center">
          {!uploadResult ? (
            <>
              <Stack align="start" gap={6} className="w-full">
                <Text className="text-subtle">
                  Upload a CSV file with your technician information. The file
                  should include columns for first name, last name, email, and
                  phone number.{" "}
                  <a
                    href="/templates/technician_import_template.csv"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-link hover:underline"
                  >
                    Download a template
                  </a>
                </Text>

                {csvValidationErrors.length > 0 && (
                  <Alert color="danger">
                    <Stack className="w-full" align="start" gap={2}>
                      <Stack direction="row" gap={1} align="center">
                        <WarningIcon className="h-5 w-5" />
                        <Text variant="label">CSV file validation failed</Text>
                      </Stack>
                      <AlertDescription>
                        <Stack className="w-full" align="start" gap={2}>
                          <Text>
                            Please fix the following errors in your CSV file:
                          </Text>
                          <ul className="list-disc space-y-1 pl-4">
                            {csvValidationErrors.map((error, index) => (
                              <li key={index}>
                                <Text variant="caption">
                                  Row {error.row}, Column "{error.column}":{" "}
                                  {error.message}
                                  {error.value && ` (Value: "${error.value}")`}
                                </Text>
                              </li>
                            ))}
                          </ul>
                        </Stack>
                      </AlertDescription>
                    </Stack>
                  </Alert>
                )}

                <FileUpload
                  value={selectedFile ? [selectedFile] : []}
                  onChange={(files) => {
                    const file = files[0];
                    if (file) {
                      void handleFileSelect(file);
                    } else {
                      setSelectedFile(null);
                    }
                  }}
                  accept=".csv"
                  fileTypeName="CSV"
                  disabled={isUploading}
                  className="w-full"
                />
              </Stack>

              <DialogFooter className="self-stretch">
                <Button
                  variant="secondary"
                  onClick={handleClose}
                  disabled={isUploading}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleUpload}
                  disabled={
                    !selectedFile ||
                    csvValidationErrors.length > 0 ||
                    isUploading
                  }
                  aria-busy={isUploading}
                >
                  {!isUploading && (
                    <UploadSimpleIcon
                      className="h-4 w-4"
                      data-icon="inline-start"
                    />
                  )}
                  Upload
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <Stack align="start" gap={4} className="w-full">
                <Text variant="title" className="w-full text-center">
                  Upload Complete!
                </Text>

                <Stack className="w-full" gap={2} align="center">
                  <Text variant="display" tone="success">
                    {uploadResult.successCount}
                  </Text>
                  <Text className="text-subtle">
                    technicians uploaded successfully
                  </Text>
                </Stack>

                {uploadResult.failedRows &&
                  uploadResult.failedRows.length > 0 && (
                    <Stack className="w-full" align="start" gap={2}>
                      <Text className="text-center text-danger">
                        {uploadResult.failedRows.length} rows failed to upload
                      </Text>
                      <div className="max-h-32 overflow-y-auto text-xs text-danger">
                        {uploadResult.failedRows.map((row, index) => (
                          <div
                            key={index}
                            className="rounded bg-danger-subtle p-2"
                          >
                            <div>Row: {Object.values(row.row).join(", ")}</div>
                            <div>Error: {row.errors}</div>
                          </div>
                        ))}
                      </div>
                    </Stack>
                  )}
              </Stack>

              <DialogFooter className="self-stretch">
                <Button onClick={handleComplete} className="w-full">
                  Continue
                </Button>
              </DialogFooter>
            </>
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  );
};
